import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import apiClient from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { loadImage } from "../../common/services/uiService";

// Error message mapping dictionary
const ERROR_MESSAGES = {
  "Invalid Password": "Incorrect password. Please try again.",
  "Invalid User":
    "Username doesn't exist or is invalid. Please check and try again.",
};

const Login = ({ closeLoginForm, applicationName, isReAuth }) => {
  const history = useHistory();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    userName: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const loadingIcon = loadImage("loading_continuous.gif");

  // Function to get user-friendly error message
  const getUserFriendlyErrorMessage = (serverMessage) => {
    // Handle unauthorized user pattern
    if (serverMessage && serverMessage.includes("is not allow to login to")) {
      const username =
        serverMessage.match(/The user (.+) is not/)?.[1] || "This user";
      return `${username} is not authorized to access this application.`;
    }

    // Check for exact matches in the dictionary
    if (ERROR_MESSAGES[serverMessage]) {
      return ERROR_MESSAGES[serverMessage];
    }

    // Default fallback
    return serverMessage || "An error occurred. Please try again.";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "userName":
        if (!value.trim()) return "Please enter your username";
        if (value.length < 2) return "Username must be at least 2 characters";
        return "";
      case "password":
        if (!value) return "Please enter your password";
        if (value.length < 3) return "Password must be at least 3 characters";
        return "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear server error on change
    if (serverError) {
      setServerError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError("");

    try {
      console.log("Attempting login with:", formData.userName);

      const loginData = {
        userName: formData.userName.trim(),
        password: formData.password,
      };

      if (applicationName) {
        loginData.applicationName = applicationName;
      }

      const response = await apiClient.post("/Auth/login", loginData);

      if (response.data?.token) {
        login(response.data);

        // DON'T redirect if this is a re-authentication
        if (!isReAuth) {
          history.push("/");

          if (closeLoginForm) {
            closeLoginForm();
          }
        }
        // If isReAuth is true, the modal will close automatically via AuthContext
        // when login() is called and setShowReAuthModal(false) runs
      } else {
        setServerError("Invalid response from server");
      }
    } catch (error) {
      console.error("Login error:", error);

      if (error.response?.status === 401) {
        const serverMessage = error.response.data?.message;
        setServerError(
          serverMessage
            ? getUserFriendlyErrorMessage(serverMessage)
            : "Invalid username or password"
        );
      } else if (error.response?.status === 400) {
        const serverMessage = error.response.data?.message;
        setServerError(getUserFriendlyErrorMessage(serverMessage));
      } else if (error.response?.status === 403) {
        const serverMessage = error.response.data?.message;
        setServerError(getUserFriendlyErrorMessage(serverMessage));
      } else if (error.response?.status >= 500) {
        setServerError("Server error. Please try again later.");
      } else if (!error.response) {
        setServerError("Network error. Please check your connection.");
      } else {
        const serverMessage = error.response.data?.message;
        setServerError(
          serverMessage
            ? getUserFriendlyErrorMessage(serverMessage)
            : "An unexpected error occurred. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (closeLoginForm) {
      closeLoginForm();
    }
  };

  return (
    <div className="LogInPopup">
      <div
        className="LogInPopupInner"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "300px",
          maxWidth: "90vw",
          minWidth: "300px",
          minHeight: "350px",
          height: "auto",
          maxHeight: "80vh",
          paddingBottom: isReAuth ? "0" : "10px",
          animationName: "none",
          boxSizing: "border-box",
          overflow: "hidden",
          background: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Session Expired Header - Only show for re-authentication */}
        {isReAuth && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-600 font-medium text-xs">
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="leading-tight">
                <div>Your session has expired.</div>
                <div>Please log in again to continue.</div>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="flex flex-col justify-center items-center h-full pt-5">
          <div className="text-xl text-gray-800">Log in to your account</div>

          <div className="w-full bg-white p-6">
            <form onSubmit={handleSubmit} className="w-full">
              {/* Form fields */}
              <div className="space-y-4">
                {/* Username field */}
                <div>
                  <label
                    htmlFor="userName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Username:
                  </label>
                  <input
                    id="userName"
                    name="userName"
                    type="text"
                    placeholder="Username"
                    value={formData.userName}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    autoComplete="username"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#629ac4] transition-all ${
                      errors.userName
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                    } ${isLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  />
                  {errors.userName && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.userName}
                    </div>
                  )}
                </div>

                {/* Password field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Password:
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    autoComplete="current-password"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#629ac4] transition-all ${
                      errors.password
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                    } ${isLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  />
                  {errors.password && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </div>
                  )}
                </div>
              </div>

              {/* Error message area with fixed height */}
              <div className="h-[52px] flex items-center my-2">
                {serverError && (
                  <div className="w-full p-2 bg-red-50 rounded text-red-600 text-xs">
                    {serverError}
                  </div>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={
                  isLoading || !formData.userName.trim() || !formData.password
                }
                className={`w-full py-2 px-4 bg-[#629ac4] text-white font-medium rounded-md transition-all ${
                  isLoading || !formData.userName.trim() || !formData.password
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-[#4a7fa3] active:bg-[#3a6f93]"
                }`}
              >
                <div className="flex justify-center items-center h-5">
                  {isLoading ? (
                    <img src={loadingIcon} alt="loading" className="w-7 h-7" />
                  ) : (
                    <span>Log in</span>
                  )}
                </div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
